// src/pages/ArtworkChartsPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, Doughnut } from "react-chartjs-2";
import { supabase } from "../lib/supabaseClient";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Palette HSL élégante et équilibrée
const makePalette = (n, { s = 55, l = 62 } = {}) =>
  Array.from({ length: n }, (_, i) => `hsl(${Math.round((360 / n) * i)}, ${s}%, ${l}%)`);
// Couleur dédiée et stable pour les catégories inconnues/non renseignées
const UNKNOWN_LABELS = new Set(["__unknown__", "__not_provided__"]);
const UNKNOWN_COLOR = "#e5e7eb"; // gris clair
const greyUnknowns = (labels, colors) =>
  labels.map((lab, i) => (UNKNOWN_LABELS.has(String(lab || "").trim()) ? UNKNOWN_COLOR : colors[i]));
const parseYear = (str) => {
  const match = String(str || "").match(/\d{4}/);
  return match ? parseInt(match[0]) : null;
};

// Convertit une année (y compris "1910-1912", "c. 1955") -> libellé décennie
const toDecadeLabel = (year) => {
  const y = parseYear(year);
  if (!y) return "__unknown__";
  const d = Math.floor(y / 10) * 10;
  return `${d}s`;
};

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const ALL = "ALL";
const NONE = "NONE";

export default function ArtworkChartsPage() {
  const { t, i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const locale = language.startsWith("de") ? "de-DE" : language.startsWith("en") ? "en-US" : "fr-FR";
  const labelFor = (value) => value === "__unknown__" ? t("artworkCharts.unknown") : value === "__not_provided__" ? t("artworkCharts.notProvided") : value;
  const [userId, setUserId] = useState(null);
  const [periodData, setPeriodData] = useState({ labels: [], values: [] });

  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(ALL);

  const [artworks, setArtworks] = useState([]);

  // Données agrégées
  const [locationData, setLocationData] = useState({ labels: [], values: [] });
  const [artistData, setArtistData] = useState({ labels: [], values: [] });
  const [mediumData, setMediumData] = useState({ labels: [], values: [] });
  const [typeData, setTypeData] = useState({ labels: [], values: [] });
  const [valueByMediumData, setValueByMediumData] = useState({ labels: [], values: [] });

  // 1) Récupère userId + collections de l'utilisateur
  useEffect(() => {
    const bootstrap = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const uid = sessionData?.session?.user?.id;
      if (!uid) return;

      setUserId(uid);
      const { data: cols, error: colErr } = await supabase
        .from("collections")
        .select("id, title")
        .eq("client_id", uid)
        .order("title", { ascending: true });

      if (!colErr) {
	   const mapped = (cols || []).map((c) => ({ id: c.id, title: c.title || t("artworkCharts.untitled") }))
	   setCollections(mapped);
	 }
    };
    bootstrap();
  }, [t]);

// 2) Charge les œuvres selon le filtre de collection
useEffect(() => {
  if (!userId) return;
  const loadArtworks = async () => {
    let q = supabase
       .from("artworks")
      .select(`
        id,
        artist,
        year,
        medium,
        typeOfArt,
        value,
        estimated_value,
        collection_id,
        client_id,
        location_id
      `)
      .eq("client_id", userId);

    // Filtre collection
    if (selectedCollectionId !== ALL) {
      if (selectedCollectionId === NONE) q = q.is("collection_id", null);
      else q = q.eq("collection_id", selectedCollectionId);
    }

    const { data, error } = await q;

    if (error) {
      console.error("[ArtworkCharts] load artworks:", error);
      setArtworks([]);
      return;
    }

    setArtworks(data || []);
    console.info("✅ Œuvres chargées:", data?.length || 0);
  };

  loadArtworks();
}, [userId, selectedCollectionId]);

  // 3) Agrège pour les graphiques
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (!Array.isArray(artworks) || artworks.length === 0) {
    setMediumData({ labels: [], values: [] });
    setTypeData({ labels: [], values: [] });

    setPeriodData({ labels: [], values: [] });
    return;
  }

  const mediumMap = Object.create(null);
  const valueMediumMap = Object.create(null);
  const typeMap   = Object.create(null);
  const decadeMap = Object.create(null);
  const artistMap = Object.create(null);
  const locationMap = Object.create(null);

  for (const a of artworks) {
    // --- Artiste ---
    const artist = (typeof a?.artist === "string" ? a.artist : "")?.trim() || "__not_provided__";
    artistMap[artist] = (artistMap[artist] || 0) + 1;

    // --- Médium ---
    const m = (typeof a?.medium === "string" ? a.medium : "")?.trim() || "__not_provided__";
    mediumMap[m] = (mediumMap[m] || 0) + 1;

// --- Pondération par valeur (en €) ---
const val = Number(a?.value ?? a?.estimated_value ?? 0) || 0;
valueMediumMap[m] = (valueMediumMap[m] || 0) + val;


    // --- Type d'art ---
    const t = (typeof a?.typeOfArt === "string" ? a.typeOfArt : "")?.trim() || "__not_provided__";
    typeMap[t] = (typeMap[t] || 0) + 1;

    // --- Période (décennies) ---
    const label = toDecadeLabel(a?.year);
    decadeMap[label] = (decadeMap[label] || 0) + 1;

    // --- Valeur par lieu ---
    const valLieu = Number(a?.value ?? a?.estimated_value ?? 0) || 0;
  // index des titres de collections pour éviter d'afficher un UUID
  const colTitleById = Object.fromEntries((collections || []).map(c => [c.id, c.title]));

    const locLabel =
      (a?.location && a.location.name) ? a.location.name
      : (a?.location_name ? a.location_name
      // 👉 si pas de nom de lieu, on affiche le titre de la collection
      : (a?.collection_id && colTitleById[a.collection_id] ? colTitleById[a.collection_id]
      // dernier recours : uuid (évité dans la plupart des cas)
      : (a?.location_id ? `#${a.location_id}` : "__not_provided__")));
    locationMap[locLabel] = (locationMap[locLabel] || 0) + valLieu;
  }

  // Sorts & projection
  const mediums  = Object.entries(mediumMap).sort((a,b) => b[1]-a[1]);
  const types    = Object.entries(typeMap).sort((a,b) => b[1]-a[1]);
  const artists   = Object.entries(artistMap).sort((a,b) => b[1]-a[1]).slice(0, 30); // top 30
  const locations = Object.entries(locationMap).sort((a,b) => b[1]-a[1]).slice(0, 30); // top 30

  // Décennies : tri chronologique, "Inconnue" en dernier
  const toNum = (lab) => (lab === "__unknown__" ? Infinity : parseInt(lab, 10));
  const decades = Object.entries(decadeMap).sort((a,b) => toNum(a[0]) - toNum(b[0]));

  setMediumData({
    labels: mediums.map(([k]) => labelFor(k)),
    values: mediums.map(([,v]) => v),
  });

  setTypeData({
    labels: types.map(([k]) => labelFor(k)),
    values: types.map(([,v]) => v),
  });

// Donut : répartition de la valeur totale par médium
const valueMediums = Object.entries(valueMediumMap).sort((a, b) => b[1] - a[1]);
setValueByMediumData({
  labels: valueMediums.map(([k]) => labelFor(k)),
  values: valueMediums.map(([, v]) => v),
});


  setPeriodData({
    labels: decades.map(([k]) => labelFor(k)),
    values: decades.map(([,v]) => v),
  });

  setArtistData({
    labels: artists.map(([k]) => labelFor(k)),
    values: artists.map(([,v]) => v),
  });

  setLocationData({
    labels: locations.map(([k]) => labelFor(k)),
    values: locations.map(([,v]) => v),
  });
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [artworks]);

const doughnutOptions = useMemo(() => ({
  responsive: true,
  maintainAspectRatio: true,
  aspectRatio: 1,
  plugins: {
    legend: { 
      position: "right",
      labels: {
        font: { size: 11 },
        padding: 12,
        boxWidth: 12,
        boxHeight: 12
      }
    },
    title: { display: false },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: 12,
      bodyFont: { size: 12 },
      titleFont: { size: 13, weight: '600' },
      callbacks: {
        label: (ctx) => {
          const v = ctx.parsed;
          const total = ctx.dataset.data.reduce((s, x) => s + x, 0);
          const p = total ? ((v / total) * 100).toFixed(1) : 0;
          return `${ctx.label}: ${new Intl.NumberFormat(locale).format(v)} (${p}%)`;
        },
      },
    },
  },
}), [locale]);

  return (
    <div className="max-w-6xl mx-auto py-16 px-6">
      <div className="flex items-center justify-between mb-12 pb-6 border-b border-gray-200">
        <h1 className="text-4xl font-light text-gray-900">{t("artworkCharts.title")}</h1>

        {/* Filtre par collection */}
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-500 font-light">{t("artworkCharts.collection")}</label>
          <select
            className="border border-gray-300 rounded px-4 py-2 text-sm bg-white hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
            value={selectedCollectionId}
            onChange={(e) => setSelectedCollectionId(e.target.value)}
          >
            <option value={ALL}>{t("artworkCharts.allCollections")}</option>
            <option value={NONE}>{t("artworkCharts.noCollection")}</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Valeur totale par lieu */}
      <div className="mb-16">
        <h2 className="text-xl font-light text-gray-800 mb-6">{t("artworkCharts.charts.valueByLocation")}</h2>
        <div className="bg-white p-6 rounded border border-gray-200" style={{ height: '420px' }}>
          <Bar
            data={{
              labels: locationData.labels,
              datasets: [
                {
                  label: t("artworkCharts.datasets.totalValue"),
                  data: locationData.values,
                  backgroundColor: "#374151",
                  borderRadius: 4,
                  borderSkipped: false,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { 
                legend: { display: false }, 
                title: { display: false },
                tooltip: {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  padding: 12,
                  bodyFont: { size: 12 },
                  titleFont: { size: 13, weight: '600' }
                }
              },
              scales: {
                y: {
                  grid: { color: '#f3f4f6' },
                  ticks: { font: { size: 11 }, color: '#6b7280' }
                },
                x: {
                  grid: { display: false },
                  ticks: { font: { size: 11 }, color: '#6b7280' }
                }
              }
            }}
          />
        </div>
      </div>

      {/* Nombre d'œuvres par artiste */}
      <div className="mb-16">
        <h2 className="text-xl font-light text-gray-800 mb-6">{t("artworkCharts.charts.artworksByArtist")}</h2>
        <div className="bg-white p-6 rounded border border-gray-200" style={{ height: '500px' }}>
          <Bar
            data={{
              labels: artistData.labels,
              datasets: [
                {
                  label: t("artworkCharts.datasets.artworks"),
                  data: artistData.values,
                  backgroundColor: "#6b7280",
                  borderRadius: 4,
                  borderSkipped: false,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: "y",
              plugins: { 
                legend: { display: false }, 
                title: { display: false },
                tooltip: {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  padding: 12,
                  bodyFont: { size: 12 },
                  titleFont: { size: 13, weight: '600' }
                }
              },
              scales: {
                x: {
                  grid: { color: '#f3f4f6' },
                  ticks: { font: { size: 11 }, color: '#6b7280' }
                },
                y: {
                  grid: { display: false },
                  ticks: { font: { size: 11 }, color: '#6b7280' }
                }
              }
            }}
          />
        </div>
      </div>

      {/* Grille des doughnuts - 2 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
        
        {/* Répartition par médium */}
        <div>
          <h2 className="text-xl font-light text-gray-800 mb-6">{t("artworkCharts.charts.byMedium")}</h2>
          <div className="bg-white p-6 rounded border border-gray-200 flex justify-center items-center" style={{ minHeight: '480px' }}>
            <div style={{ width: '400px', height: '400px' }}>
              <Doughnut
                data={{
                  labels: mediumData.labels,
                  datasets: [
                    {
                      label: t("artworkCharts.datasets.artworks"),
                      data: mediumData.values,
                      backgroundColor: greyUnknowns(
                        mediumData.labels,
                        makePalette(mediumData.labels.length)
                      ),
                      borderColor: "#fff",
                      borderWidth: 2,
                      hoverOffset: 4,
                    },
                  ],
                }}
                options={doughnutOptions}
              />
            </div>
          </div>
        </div>

        {/* Répartition par type d'art */}
        <div>
          <h2 className="text-xl font-light text-gray-800 mb-6">{t("artworkCharts.charts.byType")}</h2>
          <div className="bg-white p-6 rounded border border-gray-200 flex justify-center items-center" style={{ minHeight: '480px' }}>
            <div style={{ width: '400px', height: '400px' }}>
              <Doughnut
                data={{
                  labels: typeData.labels,
                  datasets: [
                    {
                      label: t("artworkCharts.datasets.artworks"),
                      data: typeData.values,
                      backgroundColor: greyUnknowns(
                        typeData.labels,
                        makePalette(typeData.labels.length, { s: 52, l: 64 })
                      ),
                      borderColor: "#fff",
                      borderWidth: 2,
                      hoverOffset: 4,
                    },
                  ],
                }}
                options={doughnutOptions}
              />
            </div>
          </div>
        </div>

        {/* Répartition par période (décennies) */}
        <div>
          <h2 className="text-xl font-light text-gray-800 mb-6">{t("artworkCharts.charts.byPeriod")}</h2>
          <div className="bg-white p-6 rounded border border-gray-200 flex justify-center items-center" style={{ minHeight: '480px' }}>
            <div style={{ width: '400px', height: '400px' }}>
              <Doughnut
                data={{
                  labels: periodData.labels,
                  datasets: [
                    {
                      label: t("artworkCharts.datasets.artworks"),
                      data: periodData.values,
                      backgroundColor: greyUnknowns(
                        periodData.labels,
                        makePalette(periodData.labels.length, { s: 54, l: 63 })
                      ),
                      borderColor: "#fff",
                      borderWidth: 2,
                      hoverOffset: 4,
                    },
                  ],
                }}
                options={doughnutOptions}
              />
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-400 text-center">
            {t("artworkCharts.periodHelp")}
          </p>
        </div>

        {/* Répartition de la valeur totale (€) par médium */}
        <div>
          <h2 className="text-xl font-light text-gray-800 mb-6">{t("artworkCharts.charts.byTotalValue")}</h2>
          {valueByMediumData.labels.length ? (
            <div className="bg-white p-6 rounded border border-gray-200 flex justify-center items-center" style={{ minHeight: '480px' }}>
              <div style={{ width: '400px', height: '400px' }}>
                <Doughnut
                  data={{
                    labels: valueByMediumData.labels,
                    datasets: [
                      {
                        label: t("artworkCharts.datasets.totalValue"),
                        data: valueByMediumData.values,
                        backgroundColor: greyUnknowns(
                          valueByMediumData.labels,
                          makePalette(valueByMediumData.labels.length)
                        ),
                        borderColor: "#fff",
                        borderWidth: 2,
                        hoverOffset: 4,
                      },
                    ],
                  }}
                  options={doughnutOptions}
                />
              </div>
            </div>
          ) : (
            <div className="bg-white p-6 rounded border border-gray-200 flex justify-center items-center" style={{ minHeight: '480px' }}>
              <p className="text-sm text-gray-400">{t("artworkCharts.noValueData")}</p>
            </div>
          )}
        </div>

      </div>

    </div>
  );
}